'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { getCookie, Z_SESSION_COOKIE_KEY } from '@zoomit/primitives';

import { getOverlays, postOverlaysClickCount, postOverlaysVisitCount } from '../../apis/overlays';
import { isMobile } from '../../constants/media-query';
import type { BannerSpec } from '../../types/modules';
import type { OverlayProps } from '../../types/overlays';
import { LargeTemplate } from './LargeTemplate';
import { SmallTemplate } from './SmallTemplate';

export const Overlay = (props: OverlayProps) => {
  const {
    autoOpenDelay = 2000,
    pageId,
    mobileStickyBannerSpec,
    mobileSmallTemplateDefaultBottom = 16,
  } = props;
  const modal = useOverlayTriggerState({});

  const pathname = usePathname();

  let timeoutId: NodeJS.Timeout;

  const overlayStorageKeyMaker = (id: string) => `overlay-session-${id}`;

  const getCurrentSessionCount = (id: string) =>
    parseInt(localStorage.getItem(overlayStorageKeyMaker(id)) || '0', 10);

  const sessionId = () => getCookie(Z_SESSION_COOKIE_KEY) ?? '';

  const checkScheduleAndOpen = async ({
    maxSessionPerUser,
    id,
  }: {
    maxSessionPerUser: number;
    id: string;
  }) => {
    if (!id) return;
    const overlayStorageKey = overlayStorageKeyMaker(id);

    const hasBeenClosed = sessionStorage.getItem(overlayStorageKey) === 'closed';

    if (hasBeenClosed) return;

    const shouldOpen = !maxSessionPerUser || getCurrentSessionCount(id) < maxSessionPerUser;

    if (shouldOpen) {
      if (sessionId) {
        await postOverlaysVisitCount({
          id,
          session: sessionId(),
        });
      }
      modal.open();
    }
  };

  const { data } = useQuery({
    queryKey: ['overlays'],
    queryFn: async () => {
      try {
        const response = await getOverlays({
          pageId,
          pageUrl: encodeURIComponent(pathname ?? ''),
          platform: isMobile ? 'mobile' : 'desktop',
        });

        if (!response.hasOverlay) return null;

        timeoutId = setTimeout(
          () =>
            checkScheduleAndOpen({
              maxSessionPerUser: response?.overlay?.maxSessionPerUser,
              id: response?.overlay?.id,
            }),
          autoOpenDelay,
        );

        return response.overlay;
      } catch (error) {
        console.info('there is no overlay for this page:', error);
      }
    },
    retry: false,
  });

  const handleNavigate = async (id: string) => {
    if (sessionId) {
      await postOverlaysClickCount({
        id,
        session: sessionId(),
      });
    }
    handleClose(id);
  };

  const handleClose = async (id: string) => {
    localStorage.setItem(overlayStorageKeyMaker(id), (getCurrentSessionCount(id) + 1).toString());
    sessionStorage.setItem(overlayStorageKeyMaker(id), 'closed');

    modal.close();
  };

  const getMobileSmallTemplateBottomPosition = (
    bannerSpec: BannerSpec | undefined,
    defaultPosition: number,
  ) => {
    const bannerHeight = (bannerSpec?.maxHeight || 0) * (bannerSpec?.rowCount || 0);
    return bannerHeight + defaultPosition;
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  if (!modal.isOpen || data === null || !data?.id) return null;

  if (data.template === 'small') {
    return (
      <SmallTemplate
        bottomPosition={getMobileSmallTemplateBottomPosition(
          mobileStickyBannerSpec,
          mobileSmallTemplateDefaultBottom,
        )}
        data={data}
        handleClose={() => handleClose(data?.id)}
        handleNavigate={() => handleNavigate(data?.id)}
      />
    );
  }

  return (
    <LargeTemplate
      data={data}
      handleClose={() => handleClose(data?.id)}
      handleNavigate={() => handleNavigate(data?.id)}
    />
  );
};
